import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import VippstarLogo from "../../assets/vippstar_logo.png";
import PatientSidebar from "../../components/PatientSidebar";
import socket from "../../socket";
import { pathPatientActivityPlan, pathPatientCaregiverSupport, API_URL } from "../../config/constants";
import ImgMotor          from "../../assets/activity_types/motor.png";
import ImgSelfRegulatory from "../../assets/activity_types/self_regulatory.png";
import ImgCognitive      from "../../assets/activity_types/cognitive.png";
import ImgSensory        from "../../assets/activity_types/sensory.png";
import ImgInteractive    from "../../assets/activity_types/interactive.png";
import ImgTherapist      from "../../assets/therapist.png";
import ImgQuestionnaire  from "../../assets/questionnaire.png";
import {
  Layout, Main,
  TopBar, TopBarLogo, TopBarContent,
  SectionTitle,
  CardsRow, ActivityCard, CardIllustration, CardBody, CardTypeName, CardSubtext,
  CardButton,
  BottomRow, Panel, PanelWide, PanelTitle, PanelButton,
  TherapistAvatar, MessageText,
  WeeksRow, WeekItem, WeekLabel, WeekStats,
  QuestionnaireIcon, QuestionnaireTitle, QuestionnaireText,
} from "./styles";

const ACTIVITY_TYPES = [
  { id: 1, name: "Motor",           img: ImgMotor,          bg: "#edf6f9" },
  { id: 2, name: "Self-regulatory", img: ImgSelfRegulatory, bg: "#f0f7f4" },
  { id: 3, name: "Cognitive",       img: ImgCognitive,      bg: "#fdf6ec" },
  { id: 4, name: "Sensory",         img: ImgSensory,        bg: "#f0f9f5" },
  { id: 5, name: "Interactive",     img: ImgInteractive,    bg: "#fdf0f5" },
];

const ROADTRIP_WEEKS = [
  { week: 1, percent: 50,  activities: "4/8 Activities",  time: "28 min"     },
  { week: 2, percent: 125, activities: "10/8 Activities", time: "1 h 23 min" },
  { week: 3, percent: 25,  activities: "2/8 Activities",  time: "12 min"     },
  { week: 4, percent: 100, activities: "12/8 Activities", time: "1 h 06 min" },
  { week: 5, percent: 25,  activities: "2/8 Activities",  time: "12 min"     },
];

const R    = 34;
const CIRC = 2 * Math.PI * R;

const CircularProgress = ({ percent }) => {
  const capped   = Math.min(percent, 100);
  const offset   = CIRC * (1 - capped / 100);
  const achieved = percent >= 100;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88">
      <circle cx="44" cy="44" r={R} fill="none" stroke="#dde8f0" strokeWidth="7" />
      <circle
        cx="44" cy="44" r={R}
        fill="none"
        stroke={achieved ? "#1b3a6b" : "#26A9B6"}
        strokeWidth="7"
        strokeLinecap="round"
        strokeDasharray={CIRC}
        strokeDashoffset={offset}
        transform="rotate(-90 44 44)"
      />
      <text x="44" y="49" textAnchor="middle" fontSize="13" fontWeight="700"
        fill={achieved ? "#1b3a6b" : "#333"}>
        {percent}%
      </text>
    </svg>
  );
};

const PatientHome = () => {
  const { t } = useTranslation();
  const history      = useHistory();
  const token        = localStorage.getItem("token");

  const [countByType, setCountByType] = useState({});
  const [therapist, setTherapist] = useState(null);


  useEffect(() => {
    fetch(`${API_URL}/me/activity-plan`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        const counts = {};
        data.forEach((a) => {
          counts[a.activity_type_id] = (counts[a.activity_type_id] || 0) + 1;
        });
        setCountByType(counts);
      })
      .catch((err) => console.error("PatientHome activity-plan fetch error:", err));

    fetch(`${API_URL}/me/therapist`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => { if (!data.error) setTherapist(data); })
      .catch(() => {});
  }, []); // eslint-disable-line

  // El terapeuta puede editar el mensaje fijo mientras esta pantalla está abierta.
  useEffect(() => {
    const onMessageUpdated = (data) =>
      setTherapist((prev) => (prev ? { ...prev, therapist_message: data.therapist_message } : prev));
    socket.on("therapistMessageUpdated", onMessageUpdated);
    return () => socket.off("therapistMessageUpdated", onMessageUpdated);
  }, []);


  return (
    <Layout>
      <TopBar>
        <TopBarLogo>
          <img src={VippstarLogo} alt="Vippstar" style={{ width: 80 }} />
        </TopBarLogo>
        <TopBarContent />
      </TopBar>

      <PatientSidebar activeNav="home" />

      <Main>
        <SectionTitle>{t("patientHome.weeklyActivities")}</SectionTitle>

        <CardsRow>
          {ACTIVITY_TYPES.map((type) => (
            <ActivityCard key={type.id} onClick={() => history.push(pathPatientActivityPlan, { typeId: type.id })}>
              <CardIllustration $bg={type.bg}>
                <img src={type.img} alt={type.name}
                  style={{ width: "100%", height: "100%", objectFit: "contain", padding: "12px", boxSizing: "border-box" }} />
              </CardIllustration>
              <CardBody>
                <CardTypeName>{type.name}</CardTypeName>
                <CardSubtext>{countByType[type.id] ?? 0} Activities</CardSubtext>

                <CardButton>{type.name} activities</CardButton>
              </CardBody>
            </ActivityCard>
          ))}
        </CardsRow>

        <BottomRow>
          <Panel>
            <PanelTitle>{t("patientHome.therapistMessage")}</PanelTitle>
            <TherapistAvatar>
              <img src={ImgTherapist} alt="Therapist"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} />
            </TherapistAvatar>
            <MessageText>
              {therapist?.therapist_message || t("patientHome.noMessage")}
            </MessageText>
            <PanelButton onClick={() => history.push(pathPatientCaregiverSupport)}>
              {t("patientHome.caregiverSupport")}
            </PanelButton>
          </Panel>

          <PanelWide>
            <PanelTitle>{t("patientHome.roadtrip")}</PanelTitle>
            <WeeksRow>
              {ROADTRIP_WEEKS.map(({ week, percent, activities, time }) => (
                <WeekItem key={week}>
                  <CircularProgress percent={percent} />
                  <WeekLabel>Week {week}</WeekLabel>
                  <WeekStats>{activities}<br />{time}</WeekStats>
                </WeekItem>
              ))}
            </WeeksRow>
            <PanelButton>{t("patientHome.summary")}</PanelButton>
          </PanelWide>

          <Panel>
            <PanelTitle>{t("patientHome.weeklyQuestionnaire")}</PanelTitle>
            <QuestionnaireIcon>
              <img src={ImgQuestionnaire} alt="Questionnaire" style={{ width: 72, objectFit: "contain" }} />
            </QuestionnaireIcon>
            <QuestionnaireTitle>{t("patientHome.questionnaireReady")}</QuestionnaireTitle>
            <QuestionnaireText>
              You have a pending form for this week. Please take a moment to complete it.
            </QuestionnaireText>
            <PanelButton>{t("patientHome.fillForm")}</PanelButton>
          </Panel>
        </BottomRow>
      </Main>
    </Layout>
  );
};

export default PatientHome;
